'use client';

import { Box, HStack, Icon, IconButton, useBreakpointValue } from "@chakra-ui/react";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { useEffect, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { Skeleton } from "../ui/skeleton";

interface ProductGalleryCarouselProps {
  images: string[];
  alt: string;
}

export const ProductGalleryCarousel: React.FC<ProductGalleryCarouselProps> = ({ images, alt }) => {
  const [ loaded, setLoaded ] = useState(false)
  const [ imagesLoaded, setImagesLoaded ] = useState(0);
  const [ currentSlide, setCurrentSlide ] = useState(0);
  const needLoadedSlides = useBreakpointValue({ base: 1, md: 2 })
  
  const slidesPerView = useBreakpointValue({
    base: 1,   // мобильные
    md: 2,     // средние и большие экраны
  });

  const [ sliderRef, instanceRef ] = useKeenSlider<HTMLDivElement>({
    slides: {
      perView: slidesPerView,
    },
    loop: false,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    }
  });

  const ready = (images.length > 1 && imagesLoaded >= (needLoadedSlides ?? 1))
            || (images.length === 1 && imagesLoaded === 1)
            && images.length > 0;
  
  useEffect(() => {
    if (ready) {
      instanceRef.current?.update();
    }
  }, [ready]);


  const sliderSkeleton = (
    <Box
      position={'absolute'}
      inset={0}
      zIndex={1}
    >
      <HStack h={'full'} gap="4px">
        <Skeleton display={{ base: "none", md: "block" }} w="50%" h="100%" aspectRatio={"4/5"} borderRadius={"none"} />
        <Skeleton w={{ base: "100%", md: "50%" }} h="100%" aspectRatio={"4/5"} borderRadius={"none"} />
      </HStack>
    </Box>
  );

  return (
    <Box position="sticky" top={0} minH={{base: '560px', md: 'auto'}} >
      {!ready && (
        sliderSkeleton
      )}
      <Box ref={sliderRef} className="keen-slider" visibility={ready ? "visible" : "hidden"} gap={"4px"} minH={{base: '560px', md: 'auto'}} >
        {images.length
          ? [...images].map((src, idx) => (
            <Box
              className="keen-slider__slide"
              minH={'auto !important'}
              key={src}
              w="100%"
              position="relative"
              aspectRatio={'4/5'}
              overflow="hidden"
            >
              <Image
                src={src}
                alt={alt}
                priority={idx < (needLoadedSlides ?? 1)}
                fill
                sizes="100vw"
                style={{ objectFit: "cover" }}
                quality={50}
                onLoadingComplete={() => setImagesLoaded((count) => count + 1)}
              />
            </Box>
          ))
          : (
            <Box className="keen-slider__slide">
              <Image src="/placeholder.webp" alt="placeholder" width={0} height={0} sizes="100vw" style={{ width: "100%", height: "auto" }} />
            </Box>
          )
        }
      </Box>
      {/* Стрелки навигации */}
      {loaded && instanceRef.current && (
        <>
          <HStack position="absolute" top="50%" left={2} right={2} justify="space-between" zIndex={2}>
            <IconButton
              aria-label="Prev"
              onClick={() => instanceRef.current?.prev()}
              variant="plain"
              disabled={currentSlide === 0}
            >
              <Icon as={MdChevronLeft} boxSize={10} />
            </IconButton>
            <IconButton
              aria-label="Next"
              onClick={() => instanceRef.current?.next()}
              variant="plain"
              disabled={
                currentSlide ===
                instanceRef.current.track.details.slides.length - 1
              }

            >
              <Icon as={MdChevronRight} boxSize={10} />
            </IconButton>
          </HStack>
          {/* // TODO: Точки навигации */}
          {/* <HStack justify="center" mt={2}>
            {images.map((_, idx) => (
              <Box
                key={idx}
                w={3}
                h={3}
                borderRadius="full"
                bg={idx === currentSlide ? "blue.500" : "gray.300"}
                cursor="pointer"
                onClick={() => instanceRef.current?.moveToIdx(idx)}
              />
            ))}
          </HStack> */}
        </>
      )}
    </Box>
  );
};